import { CharacterCard, CardState } from '../types';

/**
 * カードの表示に使用する日付を決定する
 * ローディング中やエラー状態のカードは作成日を使用し、
 * 完成したカードのみEXIF日付を使用する
 */
export const getCardDisplayDate = (card: CharacterCard): Date => {
  // カードが完成していない場合（ローディング中、エラー状態など）は作成日を使用
  if (card.status !== CardState.SUCCESS && card.status !== CardState.SAVING) {
    return card.createdAt || new Date();
  }

  // 完成したカードの場合、EXIF日付があれば使用、なければ作成日
  try {
    if (card.exifData?.createDate) {
      const exifDate = card.exifData.createDate instanceof Date 
        ? card.exifData.createDate 
        : new Date(card.exifData.createDate);
      
      // 有効な日付かチェック
      if (!isNaN(exifDate.getTime())) {
        return exifDate;
      }
    }
  } catch {
    // EXIF日付の解析に失敗した場合は作成日にフォールバック
  }

  return card.createdAt || new Date();
};

/**
 * カレンダービュー用のソート関数
 * カードの状態に応じて適切な日付でソートする
 */
export const sortCardsForCalendar = (cards: CharacterCard[]): CharacterCard[] => {
  return [...cards].sort((a, b) => {
    const dateA = getCardDisplayDate(a);
    const dateB = getCardDisplayDate(b);
    
    // 新しい順でソート
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * フィードビュー用のソート関数
 * 常に投稿日（createdAt）でソートする
 */
export const sortCardsForFeed = (cards: CharacterCard[]): CharacterCard[] => {
  return [...cards].sort((a, b) => {
    const dateA = a.createdAt || new Date();
    const dateB = b.createdAt || new Date();

    // 新しい順でソート
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * カードがEXIF日付に移動したかチェックする
 * カードが完成した時にEXIF日付と作成日が異なる場合にtrueを返す
 */
export const checkCardDateMove = (card: CharacterCard): { moved: boolean; originalDate?: Date; exifDate?: Date } => {
  // カードが完成していない場合は移動なし
  if (card.status !== CardState.SUCCESS) {
    return { moved: false };
  }

  const createdAt = card.createdAt || new Date();
  
  try {
    if (card.exifData?.createDate) {
      const exifDate = card.exifData.createDate instanceof Date 
        ? card.exifData.createDate 
        : new Date(card.exifData.createDate);
      
      // 有効な日付かチェック
      if (!isNaN(exifDate.getTime())) {
        // 日付が異なる場合（1日以上の差がある場合）
        const daysDiff = Math.abs(exifDate.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 1) {
          return { 
            moved: true, 
            originalDate: createdAt, 
            exifDate: exifDate 
          };
        }
      }
    }
  } catch {
    // EXIF日付の解析に失敗した場合は移動なし
  }

  return { moved: false };
};